import { Fragment } from 'react';

import { selectClosureTimeslots } from 'state/detourSlices';
import { useAppSelector } from 'state/hooks';
import { selectUser } from 'state/userSlice';
import { selectCurrentProjectInfo, selectTimeOfDay } from 'state/workflowSlice';

import { KM_TO_MILES, formatSpeed } from 'features/common/utils';
import { SegmentNameAndDirectionalityInfo } from 'features/map/tooltips/MapSegmentTooltip';

import { Segment } from '../../../appTypes';
import { formatClosureOffsetLabel } from '../utils';

import styles from 'features/map/Map.module.css';

export function DetourMapResultsTooltip({ segment }: { segment: Segment }) {
  const timeOfDay = useAppSelector(selectTimeOfDay);
  const userProject = useAppSelector(selectCurrentProjectInfo);
  const timeslots = useAppSelector(selectClosureTimeslots);
  const user = useAppSelector(selectUser);

  const timeOfDayOffset = timeslots[timeOfDay]?.slotIndex;
  const time = formatClosureOffsetLabel(timeOfDay, timeslots);
  const { has_hidden_features } = user;
  const NA = 'No sample';

  const chunks = [];
  if (segment) {
    function indexIntoProperty(prefix) {
      const prop_key = `${prefix}${timeOfDayOffset}`;
      // @ts-ignore segment?.state is actually a mapboxgl queried segment which can have state
      const value = segment?.properties[prop_key] || segment?.state[prop_key];
      if (!value || value === '-1') {
        return undefined;
      }
      return value.toString();
    }

    function formatSpeeds(value) {
      if (!value || value.toString() === '-1') {
        return NA;
      }
      return formatSpeed(value * KM_TO_MILES, userProject.uses_metric);
    }

    function formatCounts(value) {
      if (!value) {
        return NA;
      }
      if (value < 100) {
        return '< 100';
      } else if (value < 10000) {
        return (Math.floor(value / 100) * 100).toString();
      } else {
        return (Math.floor(value / 1000) * 1000).toString();
      }
    }
    const counts = indexIntoProperty('c');
    const speeds = indexIntoProperty('s');
    const typicalCounts = indexIntoProperty('tc');
    const typicalSpeeds = indexIntoProperty('ts');
    const freeflowSpeed = formatSpeeds(
      segment.properties.freeflow_speed_kph ||
        segment.properties.vnrt_freeflow_kph,
    );
    chunks.push(
      <tr key="title">
        <td colSpan={2}>
          <SegmentNameAndDirectionalityInfo
            cardinal={segment.properties.cdld}
            hwc={segment.properties.hwc}
            name={segment.properties.name}
          />
        </td>
      </tr>,
    );
    if (has_hidden_features) {
      chunks.push(
        <Fragment key="speed">
          <tr>
            <td>
              <b>Speed at {time}</b>
            </td>
            <td>
              <b>{formatSpeeds(speeds)}</b>
            </td>
          </tr>
          <tr>
            <td>Typical speed</td>
            <td>{formatSpeeds(typicalSpeeds)}</td>
          </tr>
          <tr className="space-under">
            <td>Freeflow speed</td>
            <td>{freeflowSpeed}</td>
          </tr>
        </Fragment>,
      );
    }
    chunks.push(
      <Fragment key="volume">
        <tr>
          <td>
            <b>Scenario volume</b>
          </td>
          <td>
            <b>{formatCounts(counts)} vph</b>
          </td>
        </tr>
        <tr>
          <td>Typical volume</td>
          <td>{formatCounts(typicalCounts)} vph</td>
        </tr>
      </Fragment>,
    );
  }

  if (chunks.length > 0) {
    return (
      <div className={styles.map_tooltip_container}>
        <table>
          <tbody>{chunks}</tbody>
        </table>
      </div>
    );
  }
  return null;
}
